import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already logged in on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    if (token && user) {
      try {
        setCurrentUser(JSON.parse(user));
        // Set auth header for all future requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch (e) {
        console.error('Error parsing stored user data', e);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    
    setLoading(false);
  }, []);

  // Login function
  const login = async (emailOrPhone, password) => {
    setError(null);
    try {
      const response = await axios.post('/api/auth/login', {
        emailOrPhone,
        password
      });
      
      const { token, user } = response.data;
      
      // Store auth data
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // Set auth header for all future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setCurrentUser(user);
      return user;
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
      throw err;
    }
  };

  // Register function
  const register = async (userData) => {
    setError(null);
    try {
      const response = await axios.post('/api/auth/register', userData);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
      throw err;
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setCurrentUser(null);
  };

  // Verify two-factor authentication
  const verifyTwoFactor = async (emailOrPhone, code, rememberDevice = false) => {
    setError(null);
    try {
      const response = await axios.post('/api/auth/verify-2fa', {
        emailOrPhone,
        code,
        rememberDevice
      });
      
      const { token, user } = response.data;
      
      // Store auth data
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // Set auth header for all future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      setCurrentUser(user);
      return user;
    } catch (err) {
      setError(err.response?.data?.message || 'Verification failed. Please try again.');
      throw err;
    }
  };

  // Request password reset
  const requestPasswordReset = async (email) => {
    setError(null);
    try {
      const response = await axios.post('/api/auth/forgot-password', { email });
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Password reset request failed. Please try again.');
      throw err;
    }
  };

  // Reset password with token
  const resetPassword = async (token, newPassword) => {
    setError(null);
    try {
      const response = await axios.post('/api/auth/reset-password', { 
        token, 
        newPassword 
      });
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Password reset failed. Please try again.');
      throw err;
    }
  };

  // Update user profile
  const updateProfile = async (userData) => {
    setError(null);
    try {
      const response = await axios.put('/api/users/profile', userData);
      
      // Update stored user data
      localStorage.setItem('user', JSON.stringify(response.data));
      setCurrentUser(response.data);
      
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Profile update failed. Please try again.');
      throw err;
    }
  };

  // Helper function to set user and token (for Google OAuth)
  const setUser = (user) => {
    setCurrentUser(user);
  };

  const setToken = (token) => {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const value = {
    currentUser,
    loading,
    error,
    login,
    register,
    logout,
    verifyTwoFactor,
    requestPasswordReset,
    resetPassword,
    updateProfile,
    setUser,
    setToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
